// @flow

import _ from 'lodash';
import { routeTo } from 'redux-router-kit';
import { $none, $set, update } from 'qim';

import { deduplicateSlashes } from '@zapier/url-utils';
import { snakeCaseKeysDeep } from '@zapier/common-utils';

import * as Account from 'app/entities/Account';
import * as CliApp from 'app/entities/CliApp';
import * as CliAppDefinition from 'app/entities/CliAppDefinition';
import * as EnvironmentVariable from 'app/entities/EnvironmentVariable';
import * as LegacyApp from 'app/entities/LegacyApp';
import * as Profile from 'app/entities/Profile';
import ActionTypes from 'app/developer-v3/ActionTypes';
import Data from 'app/developer-v3/Data';
import TrackActions from 'app/track/TrackActions';
import WbData from 'app/developer/Data';
import { cleanObjectOfEmptyValues } from 'app/common/CommonUtils';
import { getParam } from 'app/common/utils/RouterSelectors';
import { mergeDefinitions } from 'app/developer-v3/utils/mergeDefinitions';
import { notify } from 'app/developer-v3/store/shared/actions';
import {
  BASE_APP_URL_LEGACY,
  NEW_APP_DEFINITION,
  NEW_SERVICE_FIELD,
  SERVICE_ROUTES,
  SERVICE_TYPES,
  TRACKING_EVENTS,
} from 'app/developer-v3/utils/constants';
import {
  cleanDefinition,
  currentAppDefinitionEntity,
  currentAppDefinitionEntityId,
  currentAppId,
  currentAppVersion,
  currentAuthFields,
  currentDefinitionOverride,
  currentLegacyTrigger,
  currentServiceInputFields,
  currentServiceUrl,
  currentTrigger,
  lifecycleErrorMessage,
  lifecycleHasFinished,
} from 'app/developer-v3/utils/selectors';
import {
  getAppVersionUrl,
  includeInputFieldsInOperation,
  mutateAuthFieldsInTestRequest,
  titleize,
} from 'app/developer-v3/utils/utils';

import type {
  ActionType,
  AppForm,
  AppFormType,
  AppService,
  ObjectType,
  OperationMethodType,
  PartialDefinition,
  ServiceRoute,
  ServiceSettings,
  ServiceType,
} from 'app/developer-v3/types';
import type { Dispatch, GetState, State, StringMap } from 'app/common/types';
import type { FieldSchema } from 'app/developer-v3/platformSchema/field';
import type { LegacyTriggerOperationSchema } from 'app/developer-v3/platformSchema/legacy';
import type { TestData } from 'app/developer-v3/components/RequestTester';
import type {
  TriggerOperationSchema,
  TriggerSchema,
} from 'app/developer-v3/platformSchema/trigger';

const { searchOrCreates: SEARCH_OR_CREATES } = SERVICE_TYPES;
const { actions: ACTIONS, triggers: TRIGGERS } = SERVICE_ROUTES;

export const trackEvent = (message: string, data?: {}, wait?: number) =>
  TrackActions.track(message, snakeCaseKeysDeep(data), wait);

export const updateOverrideWithService = ({
  serviceType,
  service,
  serviceLegacy,
  state,
}: {
  serviceType: ServiceType,
  service: ServiceSettings,
  serviceLegacy?: ServiceSettings,
  state: State,
}) =>
  update(
    [
      [serviceType, service.key, $set(service)],
      ...(serviceLegacy
        ? [['legacy', serviceType, service.key, $set(serviceLegacy)]]
        : []),
    ],
    currentDefinitionOverride(state)
  );

const getServiceType = (type: ServiceRoute | ServiceType, state): ServiceType =>
  // Flow doesn't recognize the constant and thinks if the ternary evalutates false, type could still be 'actions`
  type === 'actions' ? getParam('actionType', state) : type;

export const setCurrentAppId = (appId: string | number) => {
  return {
    type: ActionTypes.SET_CURRENT_APP_ID,
    appId,
  };
};

export const setCurrentAppVersion = appVersion => ({
  type: ActionTypes.SET_CURRENT_APP_VERSION,
  appVersion,
});

export const setCleanDefinition = (definition: {}) => ({
  type: ActionTypes.SET_CLEAN_DEFINITION,
  definition,
});

export const setCurrentAppDefinition = (appDefinition: {}) => ({
  type: ActionTypes.SET_CURRENT_APP_DEFINITION,
  appDefinition,
});

const reportFailedEntityAction = (
  dispatch: Dispatch,
  getState: GetState,
  entityId: string | number,
  selectors: Object
) => {
  const state = getState();
  const fetchedEntity = selectors.all.entity(entityId, state);
  const isFailure = selectors.isFailure(fetchedEntity);

  if (isFailure) {
    const statusMessage = selectors.statusMessage(fetchedEntity);
    dispatch(notify.failure(statusMessage));
  }

  return isFailure;
};

export const loadAppWithLatestVersion = (
  appId: string,
  versionNumber?: string
) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch(setCurrentAppId(appId));
    await dispatch(CliApp.actions.loadEntity(null, appId));
    const state = getState();
    const currentApp = CliApp.selectors.all.entity(appId, state);

    if (CliApp.selectors.isFailure(currentApp)) {
      return;
    }

    if (versionNumber) {
      dispatch(setCurrentAppVersion(versionNumber));
    } else {
      const latestVersion = CliApp.selectors.latestVersion(currentApp);
      dispatch(setCurrentAppVersion(latestVersion));
    }

    await dispatch(
      CliAppDefinition.actions.loadEntity(null, currentAppVersion(getState()))
    );

    dispatch(EnvironmentVariable.actions.resetAndLoadCollection());

    // save fresh version for use later
    const freshDefinition = currentDefinitionOverride(getState());
    await dispatch(setCleanDefinition(freshDefinition));
  };
};

export const loadEnvironmentForVersion = () => {
  // should this take a version?
  return async (dispatch: Dispatch) => {
    dispatch(EnvironmentVariable.actions.loadCollection());
  };
};

export const loadAppAndAllVersions = (appId: number) => {
  return (dispatch: Dispatch) =>
    Promise.all([
      dispatch(CliApp.actions.loadEntity(null, appId)),
      dispatch(CliAppDefinition.actions.loadCollection()),
    ]);
};

export const getAppFormFields = (formId: AppForm) => ({
  type: ActionTypes.GET_APP_FORM,
  promise: Data.getAppFormFields(formId),
  formId,
  resultKey: 'fields',
});

const startLifecycleRequest = () => ({ type: ActionTypes.LIFECYCLE_REQUEST });
const completeLifecycleRequest = () => ({
  type: ActionTypes.LIFECYCLE_REQUEST_DONE,
});
const failLifecycleRequest = (errorMessage: string = 'Unknown Error') => ({
  type: ActionTypes.LIFECYCLE_REQUEST_FAIL,
  errorMessage,
});

const pullErrorMessage = (
  responseText: string,
  errorMessagePath?: Array<string> = ['errors', '0']
) => {
  const errorResponse = JSON.parse(responseText);
  return _.get(errorResponse, errorMessagePath);
};

export const resetLifecycle = () => async (dispatch: Dispatch) =>
  dispatch({ type: ActionTypes.LIFECYCLE_REQUEST_RESET });

export const updateAppDefinition = (definition: PartialDefinition) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    // First attempt to update.
    await dispatch(CliAppDefinition.actions.updateAppDefinition(definition));

    let isFailure = CliAppDefinition.selectors.isFailure(
      currentAppDefinitionEntity(getState())
    );

    if (isFailure) {
      const [statusMessage] = CliAppDefinition.selectors.statusMessages(
        currentAppDefinitionEntity(getState())
      );
      if (_.isPlainObject(statusMessage) && statusMessage.code === 409) {
        // Stumbled upon a conflict, try to merge.
        const newServerDefinition = statusMessage.definition;
        const { mergedDefinition, success, paths } = mergeDefinitions(
          definition,
          cleanDefinition(getState()),
          newServerDefinition.definition_override
        );

        if (success) {
          dispatch(trackEvent(TRACKING_EVENTS.SUCCESSFUL_MERGE));

          // Second attempt to update.
          await dispatch(
            CliAppDefinition.actions.updateAppDefinition(
              mergedDefinition,
              newServerDefinition.etag
            )
          );
          await dispatch(
            setCleanDefinition(currentDefinitionOverride(getState()))
          );

          isFailure = CliAppDefinition.selectors.isFailure(
            currentAppDefinitionEntity(getState())
          );
        } else {
          dispatch(trackEvent(TRACKING_EVENTS.FAILED_MERGE, paths));
          dispatch(
            notify.failure(
              'Sorry! There was a conflict updating this integration.'
            )
          );
        }
      } else {
        dispatch(
          notify.failure(
            CliAppDefinition.selectors.statusMessage(
              currentAppDefinitionEntity(getState())
            )
          )
        );
      }
    }

    return !isFailure;
  };
};

const saveAppImage = (entityId: number, imageFile: Object) => {
  return async (dispatch: Dispatch) => {
    try {
      return await Data.saveAppImage(entityId, imageFile);
    } catch (err) {
      return dispatch(
        notify.failure(
          'Please upload a valid image. The uploaded image was either in the incorrect format or corrupted.'
        )
      );
    }
  };
};

export const createNewAppVersion = (appId: number, versionNumber: string) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    // createEntity below uses the "current" state, so we need to set before, and revert after
    const state = getState();
    const currentAppIdToRevertTo = currentAppId(state);
    const currentAppVersionNumberToRevertTo = currentAppVersion(state);
    await dispatch(setCurrentAppId(appId));
    await dispatch(setCurrentAppVersion(versionNumber));

    const appDefEntityId = 'new-cli-app-definition';
    await dispatch(
      CliAppDefinition.actions.createCollectionUiEntity({
        id: appDefEntityId,
        definition_override: NEW_APP_DEFINITION,
        is_ui: true,
      })
    );
    await dispatch(CliAppDefinition.actions.createEntity(null, appDefEntityId));

    await dispatch(setCurrentAppId(currentAppIdToRevertTo));
    await dispatch(setCurrentAppVersion(currentAppVersionNumberToRevertTo));
  };
};

export const createNewApp = (newApp: AppFormType) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    // Create CliApp entity
    const entityId = 'new-cli-app';
    await dispatch(
      CliApp.actions.createCollectionUiEntity({
        id: entityId,
        version: NEW_APP_DEFINITION.version,
        platformVersion: NEW_APP_DEFINITION.platformVersion,
        is_ui: true,
        ...newApp,
      })
    );
    // 'create' is the form id in `developer_cli.api.resources.AppBaseResource.post`
    const response = await dispatch(
      CliApp.actions.createEntityWithParams(
        { formId: 'create' },
        null,
        entityId
      )
    );

    const shouldBail = reportFailedEntityAction(
      dispatch,
      getState,
      entityId,
      CliApp.selectors
    );
    if (shouldBail) {
      return;
    }

    const { entity } = response;

    if (entity) {
      const versionNumber = '1.0.0';
      dispatch(setCurrentAppId(entity.id));
      dispatch(setCurrentAppVersion(versionNumber));

      await dispatch(createNewAppVersion(entity.id, versionNumber));

      const baseUrl = getAppVersionUrl(entity.id, versionNumber);

      dispatch(
        notify.success(`Alright! ${newApp.title} created successfully!`)
      );

      const routeOptions = {};

      if (newApp.imageFile) {
        const { image } = await dispatch(
          saveAppImage(entity.id, newApp.imageFile)
        );
        if (image) {
          routeOptions.exit = true; // We need exit to get new custom CSS with images from backend (private-logos.css)
        }
      }

      await dispatch(routeTo(`${baseUrl}`, routeOptions));
      return;
    }

    return;
  };
};

export const updateApp = (appId: number, updatedApp: AppFormType) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    if (updatedApp.imageFile) {
      const { image } = await dispatch(
        saveAppImage(appId, updatedApp.imageFile)
      );
      if (!image) {
        return;
      }
      dispatch(
        notify.success(
          'Image uploaded successfully! Refresh your browser to see the changes.'
        )
      );
    }

    await dispatch(CliApp.actions.updateEntityData(updatedApp, appId));
    // 'meta' is the form id in `developer_cli.api.resources.AppResource.put`
    // 'isV3' is to distinguish between the old and new UI (error formatting is different)
    const response = await dispatch(
      CliApp.actions.saveEntityWithParams(
        { formId: 'meta', isV3: true },
        null,
        appId
      )
    );

    const shouldBail = reportFailedEntityAction(
      dispatch,
      getState,
      appId,
      CliApp.selectors
    );
    if (shouldBail) {
      return;
    }

    const { entity } = response;

    if (entity) {
      dispatch(
        notify.success(`Alright! ${updatedApp.title} updated successfully!`)
      );
    }

    return;
  };
};

export const saveServiceUpdates = (
  type: ServiceType,
  serviceUpdate: ServiceSettings,
  serviceUpdateLegacy?: ServiceSettings
) => async (dispatch: Dispatch, getState: GetState) =>
  dispatch(
    updateAppDefinition(
      updateOverrideWithService({
        serviceType: getServiceType(type, getState()),
        service: serviceUpdate,
        serviceLegacy: serviceUpdateLegacy,
        state: getState(),
      })
    )
  );

export const saveAction = _.partial(saveServiceUpdates, ACTIONS);
export const saveSearchOrCreate = _.partial(
  saveServiceUpdates,
  SEARCH_OR_CREATES
);

export const saveInputField = (
  serviceRoute: ServiceRoute,
  serviceUpdate: AppService,
  shouldRedirect?: boolean = true
) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const serviceType = getServiceType(serviceRoute, state);
    const oldInputFields = currentServiceInputFields(serviceType, state);
    const parsedServiceUpdate = includeInputFieldsInOperation(
      serviceUpdate,
      oldInputFields,
      serviceType
    );

    const isSuccess = await dispatch(
      saveServiceUpdates(serviceType, parsedServiceUpdate)
    );

    if (isSuccess) {
      dispatch(
        notify.success(
          getParam('inputKey', state) === NEW_SERVICE_FIELD
            ? `Congratulations! You created a new input field for ${
                parsedServiceUpdate.display.label
              } ${titleize(serviceRoute)}`
            : `All right! You successfully updated ${
                parsedServiceUpdate.display.label
              } ${titleize(serviceRoute)}`
        )
      );
    }

    return shouldRedirect
      ? dispatch(routeTo(`${currentServiceUrl(serviceType, state)}/input`))
      : undefined;
  };
};

export const saveTriggerInputField = _.partial(saveInputField, TRIGGERS);
export const saveActionInputField = _.partial(saveInputField, ACTIONS);

const deleteService = (serviceType: ServiceType, serviceKey: string) => async (
  dispatch: Dispatch,
  getState: GetState
) => {
  const state = getState();
  const definition = currentDefinitionOverride(state);
  const service = definition[serviceType];

  const newService = update([serviceKey, $none], service);
  const newDefinition = update([serviceType, $set(newService)], definition);

  const isSuccess = await dispatch(updateAppDefinition(newDefinition));
  if (isSuccess) {
    dispatch(
      notify.success(
        `${titleize(serviceType)}: ${serviceKey} has been deleted!`
      )
    );
  }
};

export const deleteAction = (
  actionType: ActionType,
  actionKey: string
) => async (dispatch: Dispatch, getState: GetState) => {
  const state = getState();
  const definition = currentDefinitionOverride(state);
  const definitionUpdates = [];

  if (actionType === SERVICE_TYPES.creates) {
    _.forEach(definition[SERVICE_TYPES.searchOrCreates], searchOrCreate => {
      if (searchOrCreate.create === actionKey) {
        definitionUpdates.push([
          SERVICE_TYPES.searchOrCreates,
          searchOrCreate.key,
          $none,
        ]);
      }
    });
  } else {
    const searchOrCreate = _.get(definition, [
      SERVICE_TYPES.searchOrCreates,
      actionKey,
    ]);
    if (searchOrCreate) {
      definitionUpdates.push([
        SERVICE_TYPES.searchOrCreates,
        searchOrCreate.key,
        $none,
      ]);
    }
  }

  if (!_.isEmpty(definitionUpdates)) {
    const newDefinition = update(definitionUpdates, definition);
    const isSuccess = await dispatch(updateAppDefinition(newDefinition));
    if (isSuccess) {
      dispatch(notify.success(`Paired action: ${actionKey} has been deleted!`));
    }
  }

  await dispatch(deleteService(actionType, actionKey));
};
export const deleteTrigger = _.partial(deleteService, TRIGGERS);
export const deleteSearchOrCreate = _.partial(deleteService, SEARCH_OR_CREATES);

export const saveCurrentAppVersion = (
  message?: string = 'Changes have been saved!'
) => (dispatch: Dispatch, getState: GetState) => {
  const entityId = currentAppDefinitionEntityId(getState());

  dispatch(
    CliAppDefinition.actions.saveEntity(
      {
        notificationDuration: Infinity,
        shouldNotify: true,
        success: message,
      },
      entityId
    )
  );
};

export const saveTriggerOperation = (
  operation: TriggerOperationSchema,
  operationLegacy?: LegacyTriggerOperationSchema
) => {
  function updateOperation(savedOperation) {
    const updated = {
      ...savedOperation,
      ...operation,
    };

    return operation.type === 'polling'
      ? _.omit(updated, [
          'performSubscribe',
          'performUnsubscribe',
          'performList',
        ])
      : updated;
  }

  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const trigger = currentTrigger(state);
    const updatedTrigger: TriggerSchema = {
      ...trigger,
      operation: updateOperation(trigger.operation),
    };
    const updatedTriggerLegacy = operationLegacy
      ? {
          ...currentLegacyTrigger(state),
          operation: {
            ...currentLegacyTrigger(state).operation,
            ...operationLegacy,
          },
        }
      : undefined;

    const definition = updateOverrideWithService({
      serviceType: 'triggers',
      service: updatedTrigger,
      serviceLegacy: updatedTriggerLegacy,
      state,
    });
    const isSuccess = await dispatch(updateAppDefinition(definition));

    if (isSuccess) {
      dispatch(notify.success('Trigger has been updated!'));
    }
  };
};

export const upsertAuthField = (field: FieldSchema, key?: string) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const fieldClean = cleanObjectOfEmptyValues(field);
    const state = getState();
    const id = currentAppId(state);
    const version = currentAppVersion(state);
    const definitionOverride = currentDefinitionOverride(state);
    const { authentication } = definitionOverride;
    const fields = _.get(authentication, ['fields'], []);
    const newFields = [
      ...(key ? _.reject(fields, ['key', key]) : fields),
      fieldClean,
    ];
    const newAuthentication = mutateAuthFieldsInTestRequest(
      authentication,
      fields,
      newFields
    );

    const newDefinition = {
      ...definitionOverride,
      authentication: newAuthentication,
    };
    const isSuccess = await dispatch(updateAppDefinition(newDefinition));

    if (isSuccess) {
      dispatch(
        notify.success(
          `Successfully ${
            key ? 'updated' : 'created'
          } field: ${fieldClean.label || fieldClean.key}!`
        )
      );
      const appVersionUrl = getAppVersionUrl(id, version);
      dispatch(routeTo(`${appVersionUrl}/authentication/edit`));
    }
  };
};

export const deleteAuthField = (key: string) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const definitionOverride = currentDefinitionOverride(state);
    const { authentication } = definitionOverride;
    const fields = currentAuthFields(state);
    const newFields = fields.filter(field => field.key !== key);
    const newAuthentication = mutateAuthFieldsInTestRequest(
      authentication,
      fields,
      newFields
    );

    const newDefinition = {
      ...definitionOverride,
      authentication: newAuthentication,
    };
    const isSuccess = await dispatch(updateAppDefinition(newDefinition));

    if (isSuccess) {
      dispatch(notify.success(`Deleted authentication field: ${key}`));
    }
  };
};

const addDefaultHeaders = authType => {
  return authType === 'oauth2'
    ? { Authorization: 'Bearer {{bundle.authData.access_token}}' }
    : {};
};

export const saveAuthenticationSchemeType = (type: string) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    if (type === 'none') {
      return;
    }

    const state = getState();
    const appId = currentAppId(state);
    const appVersion = currentAppVersion(state);
    const appDefinition = currentDefinitionOverride(state);

    const definition = {
      ...appDefinition,
      authentication: {
        type,
        test: {
          headers: addDefaultHeaders(type),
        },
      },
    };
    const isSuccess = await dispatch(updateAppDefinition(definition));
    if (isSuccess) {
      dispatch(
        trackEvent(TRACKING_EVENTS.AUTH_CONFIG_STARTED, {
          auth_type: type,
          app_id: appId,
          app_version: appVersion,
          app_version_id: currentAppDefinitionEntityId(state),
          customuser_id: Profile.selectors.all.currentProfileId(state),
          is_staff: Profile.selectors.isStaff(
            Profile.selectors.all.currentProfile(state)
          ),
        })
      );

      const appVersionUrl = getAppVersionUrl(appId, appVersion);
      dispatch(routeTo(`${appVersionUrl}/authentication/edit`));
    }
  };
};

export const deleteAuthentication = () => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const appId = currentAppId(state);
    const version = currentAppVersion(state);
    const appDefinition = currentDefinitionOverride(state);

    const definition = _.omit(appDefinition, 'authentication');
    const isSuccess = await dispatch(updateAppDefinition(definition));

    if (isSuccess) {
      dispatch(notify.success('Authentication deleted'));
    }

    const appVersionUrl = getAppVersionUrl(appId, version);
    dispatch(routeTo(`${appVersionUrl}/authentication`));
  };
};

export const deleteConnectionLabelCode = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const appDefinition = currentDefinitionOverride(state);
    const definition = _.omit(appDefinition, 'authentication[connectionLabel]');
    dispatch(updateAppDefinition(definition));
  };
};

export const deleteAuthenticationTestCode = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const appDefinition = currentDefinitionOverride(state);
    const definition = _.omit(appDefinition, 'authentication[test]');
    dispatch(updateAppDefinition(definition));
  };
};

export const createOrUpdateEnvironmentVariable = (
  key: string,
  value: string
) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const envVarEntity = EnvironmentVariable.selectors.all.entityForKeyAndVersion(
      currentAppId(state),
      currentAppVersion(state),
      key,
      state
    );

    if (!envVarEntity) {
      const newEnvVarEntityId = key;
      await dispatch(
        EnvironmentVariable.actions.createCollectionUiEntity({
          id: newEnvVarEntityId,
          key,
          value,
        })
      );
      await dispatch(
        EnvironmentVariable.actions.createEntity(null, newEnvVarEntityId)
      );
    } else {
      const envEntityId = EnvironmentVariable.selectors.id(envVarEntity);
      if (value !== EnvironmentVariable.selectors.value(envVarEntity)) {
        await dispatch(
          EnvironmentVariable.actions.updateValue(envEntityId, value)
        );

        const shouldBail = reportFailedEntityAction(
          dispatch,
          getState,
          envEntityId,
          EnvironmentVariable.selectors
        );
        if (shouldBail) {
          return;
        }
      }
    }
  };
};

export const saveAuthenticationSettings = (authSettings: Object) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const appDefinition = currentDefinitionOverride(state);
    const { envVariables, ...authConfig } = authSettings;

    for (const envKey of Object.keys(envVariables)) {
      await dispatch(
        createOrUpdateEnvironmentVariable(envKey, envVariables[envKey])
      );
    }

    const definition = {
      ...appDefinition,
      authentication: {
        ...appDefinition.authentication,
        ...authConfig,
      },
    };
    const isSuccess = await dispatch(updateAppDefinition(definition));

    if (isSuccess) {
      dispatch(notify.success('Authentication settings updated successfully!'));
    }
  };
};

export const deleteApp = (appId: number, isLegacy?: boolean = false) => {
  return async (dispatch: Dispatch) => {
    const deleteEntity = (isLegacy ? LegacyApp : CliApp).actions.deleteEntity;
    const response = await dispatch(deleteEntity(null, appId));
    if (response && response.status === 'success') {
      dispatch(notify.success('App deleted successfully!'));
    } else {
      // Unfortunately we can't know much more because response above is undefined
      dispatch(notify.failure('Unable to delete app!'));
    }
  };
};

export const deleteAppVersion = (appId: number, versionNumber: string) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    // deleteEntity below uses the "current" state, so we need to set before, and revert after
    const state = getState();
    const currentAppIdToRevertTo = currentAppId(state);
    const currentAppVersionNumberToRevertTo = currentAppVersion(state);
    await dispatch(setCurrentAppId(appId));
    await dispatch(setCurrentAppVersion(versionNumber));

    const response = await dispatch(
      CliAppDefinition.actions.deleteEntity(null, versionNumber)
    );

    if (response && response.status === 'success') {
      dispatch(notify.success(`${versionNumber} deleted successfully!`));
    } else {
      // Unfortunately we can't know much more because response above is undefined
      dispatch(notify.failure('Unable to delete version!'));
    }

    await dispatch(setCurrentAppId(currentAppIdToRevertTo));
    await dispatch(setCurrentAppVersion(currentAppVersionNumberToRevertTo));
  };
};

type LifecycleActionOptions = {
  errorMessagePath?: Array<string>,
  onError?: (
    response: XMLHttpRequest,
    dispatch: Dispatch,
    getState: GetState
  ) => void,
};

const handleLifecycleAction = (
  actionToCall: () => Promise<Object>,
  options?: LifecycleActionOptions = {}
) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const { errorMessagePath, onError } = options;

    try {
      dispatch(startLifecycleRequest());
      await dispatch(actionToCall);
      await dispatch(completeLifecycleRequest());
    } catch (response) {
      try {
        const errorMessage = pullErrorMessage(
          response.responseText,
          errorMessagePath
        );
        dispatch(failLifecycleRequest(errorMessage));
        if (typeof onError === 'function') {
          onError(response, dispatch, getState);
        }
      } catch (e) {
        console.error(e);
      }
    }
  };
};

export const batchUpdateEnvironmentVariables = (
  environmentVariables: StringMap
) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    await dispatch(
      handleLifecycleAction(
        Data.batchUpdateEnvironment.bind(
          null,
          currentAppId(state),
          currentAppVersion(state),
          environmentVariables
        )
      )
    );

    const newState = getState();
    if (lifecycleHasFinished(newState)) {
      dispatch(notify.success('Environment was Updated!'));
    } else {
      dispatch(notify.failure(lifecycleErrorMessage(newState)));
    }
    dispatch(resetLifecycle());
  };
};

export const visitActivationForm = (id: string | number, version: string) => (
  dispatch: Dispatch
) => {
  const urlToActivation = `${BASE_APP_URL_LEGACY}/cli-app/${id}/activate/${version}`;
  dispatch(routeTo(urlToActivation, { event: { shiftKey: true } }));
};

export const promoteAppVersion = (
  appId: number,
  version: string,
  changelog?: string
) =>
  handleLifecycleAction(
    Data.promoteAppVersion.bind(null, appId, version, changelog),
    {
      errorMessagePath: ['errors'],
      onError: (response, dispatch, getState) => {
        const state = getState();
        const app = CliApp.selectors.all.entity(appId, state);
        const isReadyToActivate =
          response.status === 403 &&
          JSON.parse(response.responseText).activationInfo;

        if (CliApp.selectors.isPrivate(app) && isReadyToActivate) {
          dispatch(visitActivationForm(appId, version));
        }
      },
    }
  );

export const deprecateAppVersion = (
  appId: number,
  versionNumber: string,
  date: string
) =>
  handleLifecycleAction(
    Data.deprecateAppVersion.bind(null, appId, versionNumber, date)
  );

export const migrateAppVersion = (
  appId: number,
  fromVersionNumber: string,
  toVersionNumber: string,
  percentage: number = 100,
  customerEmail?: string
) =>
  handleLifecycleAction(
    Data.migrateAppVersion.bind(
      null,
      appId,
      fromVersionNumber,
      toVersionNumber,
      percentage,
      customerEmail
    )
  );

export const cloneAppVersion = (
  appId: number,
  fromVersionNumber: string,
  toVersionNumber: string
) =>
  handleLifecycleAction(
    Data.cloneAppVersion.bind(null, appId, fromVersionNumber, toVersionNumber)
  );

export const executeRequest = (
  appId: number,
  versionNumber: string,
  objectType: ObjectType,
  objectKey: string,
  partialDefinition: PartialDefinition,
  authenticationId: string,
  operationMethod: OperationMethodType,
  testData: TestData
) => ({
  type: ActionTypes.EXECUTE_REQUEST,
  promise: Data.executeTestRequest(
    appId,
    versionNumber,
    objectType,
    objectKey,
    partialDefinition,
    authenticationId,
    operationMethod,
    testData
  ),
  appId,
  versionNumber,
  objectKey,
  objectType,
  operationMethod,
  testData,
});

export const resetTestRequest = () => ({
  type: ActionTypes.RESET_TEST_REQUEST,
});

const getFormattedDateForLogs = (addMinutes = 0) =>
  new Date(new Date().getTime() + addMinutes * 60000)
    .toISOString()
    .substr(0, 17) + '00+00:00';

const getTestLogs = (
  logsType: 'http' | 'console' | 'bundle',
  appId: number,
  versionNumber: string
) => {
  const filters = {
    start_date: getFormattedDateForLogs(-0.25),
    end_date: getFormattedDateForLogs(5),
    personal: false,
    category: logsType,
    app_version: versionNumber,
    limit: 10,
  };

  return {
    type: ActionTypes.GET_TEST_LOGS,
    promise: WbData.loadAppEvents(appId, filters),
    resultKey: 'events',
    logsType,
  };
};

export const getAllTestLogs = (appId: number, versionNumber: string) => {
  return async (dispatch: Dispatch) => {
    await Promise.all([
      dispatch(getTestLogs('http', appId, versionNumber)),
      dispatch(getTestLogs('bundle', appId, versionNumber)),
      dispatch(getTestLogs('console', appId, versionNumber)),
    ]);
  };
};

export const resetTestLogs = () => ({
  type: ActionTypes.RESET_TEST_LOGS,
});

export const setUnsavedChanges = (hasUnsavedChanges: boolean) => ({
  type: ActionTypes.SET_UNSAVED_CHANGES,
  hasUnsavedChanges,
});

export const viewAppVersion = (appVersion: string, route?: string = '') => (
  dispatch: Dispatch,
  getState: GetState
) => {
  dispatch([
    setCurrentAppVersion(appVersion),
    CliAppDefinition.actions.loadEntity(null, appVersion),
    routeTo(
      deduplicateSlashes(
        `${getAppVersionUrl(currentAppId(getState()), appVersion)}/${route}`
      )
    ),
  ]);
};

export const loadTasks = () => (dispatch: Dispatch, getState: GetState) => {
  const state = getState();
  const accountId = Account.selectors.all.currentAccountId(state);
  dispatch({
    promise: Data.loadTasks({ status: 'success', account_id: accountId }),
    type: ActionTypes.LOAD_TASKS,
  });
};
